// CabinetOverlays — Galerie photos plein écran + fiche Profil interlocuteur (v5.23.0).
import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarCheck, ChevronLeft, ChevronRight, X } from "lucide-react";
import { T, PhotoAvatar, PresenceDot, StatusPill } from "./cabinetTheme";

const API = process.env.REACT_APP_BACKEND_URL;
const overlay = { position: "fixed", inset: 0, zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center" };

// ── GALERIE PHOTOS plein écran (compteur · flèches · fermeture) ──────────────
export const PhotoGallery = ({ photos = [], startIndex = 0, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [photos.length, onClose]);
  if (!photos.length) return null;
  const nav = (d) => setIdx((i) => (i + d + photos.length) % photos.length);
  const navBtn = { width: 40, height: 40, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.14)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 };
  return (
    <div style={{ ...overlay, background: "rgba(5,10,8,0.96)", flexDirection: "column" }} data-testid="cabinet-gallery" onClick={onClose}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px" }} onClick={(e) => e.stopPropagation()}>
        <span style={{ color: "#fff", fontSize: 13, fontWeight: 800 }} data-testid="gallery-counter">{idx + 1} / {photos.length}</span>
        <button onClick={onClose} style={{ ...navBtn, width: 34, height: 34 }} aria-label="Fermer" data-testid="gallery-close"><X size={17} /></button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", maxWidth: 760, padding: "0 10px", boxSizing: "border-box" }} onClick={(e) => e.stopPropagation()}>
        {photos.length > 1 && <button onClick={() => nav(-1)} style={navBtn} aria-label="Précédente" data-testid="gallery-prev"><ChevronLeft size={19} /></button>}
        <img src={photos[idx]} alt={`Photo ${idx + 1}`} style={{ flex: 1, minWidth: 0, maxHeight: "72vh", objectFit: "contain", borderRadius: 10 }} data-testid="gallery-image" />
        {photos.length > 1 && <button onClick={() => nav(1)} style={navBtn} aria-label="Suivante" data-testid="gallery-next"><ChevronRight size={19} /></button>}
      </div>
      <div style={{ display: "flex", gap: 7, marginTop: 16, overflowX: "auto", maxWidth: "92vw", padding: "2px" }} onClick={(e) => e.stopPropagation()}>
        {photos.map((p, i) => (
          <img key={i} src={p} alt="" onClick={() => setIdx(i)} data-testid={`gallery-thumb-${i}`}
            style={{ width: 52, height: 38, objectFit: "cover", borderRadius: 7, cursor: "pointer", opacity: i === idx ? 1 : 0.45, border: i === idx ? "2px solid #34D399" : "2px solid transparent", flexShrink: 0 }} />
        ))}
      </div>
    </div>
  );
};

// ── FICHE PROFIL interlocuteur (bio · spécialités · disponibilités) ──────────
export const MemberProfileSheet = ({ slug, member, hours, onBook, onClose }) => {
  const [slots, setSlots] = useState(null);
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const date = new Date().toISOString().slice(0, 10);
    axios.get(`${API}/api/v3/cabinet/${slug}/members/${member.member_id}/slots?date=${date}`)
      .then((r) => setSlots((r.data.slots || []).filter((s) => s.available).slice(0, 4)))
      .catch(() => setSlots([]));
    return () => { document.body.style.overflow = ""; };
  }, [slug, member.member_id]);

  return (
    <div style={{ ...overlay, background: "rgba(17,24,39,0.55)", alignItems: "flex-end", justifyContent: "center" }} onClick={onClose} data-testid="member-profile-sheet">
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 560, maxHeight: "88vh", overflowY: "auto", background: "#fff", borderRadius: "18px 18px 0 0", padding: "16px 18px 22px", boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ ...T.back, width: 32, height: 32 }} aria-label="Fermer" data-testid="profile-close"><X size={15} /></button>
        </div>
        <div style={{ display: "flex", gap: 13, alignItems: "center", marginTop: -6 }}>
          <span style={{ position: "relative", flexShrink: 0 }}>
            <PhotoAvatar member={member} size={72} />
            {member.availability !== "indisponible" && <PresenceDot size={12} style={{ position: "absolute", bottom: 2, right: 2, border: "2px solid #fff" }} />}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 900, lineHeight: 1.2 }} data-testid="profile-name">
              {member.name} {member.is_demo && <span style={{ ...T.badge, ...T.badgeDemo }}>DEMO</span>}
            </div>
            <div style={{ ...T.sub, fontSize: 12.5, margin: "2px 0 5px" }}>{member.title}</div>
            <StatusPill availability={member.availability} />
          </div>
        </div>

        {member.bio && (
          <p style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.55, margin: "13px 0 0" }} data-testid="profile-bio">{member.bio}</p>
        )}

        <div style={{ marginTop: 13 }}>
          <div style={{ fontSize: 12.5, fontWeight: 900, color: "#111827", marginBottom: 6 }}>Spécialités</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="profile-specialties">
            {(member.specialties || []).map((s) => <span key={s} style={T.chip}>{s}</span>)}
          </div>
        </div>

        <div style={{ marginTop: 13 }}>
          <div style={{ fontSize: 12.5, fontWeight: 900, color: "#111827", marginBottom: 6 }}>Disponibilités</div>
          <div style={{ ...T.sub, fontSize: 11.5, marginBottom: 7 }}>🕐 {hours}</div>
          {slots === null && <div style={{ ...T.sub, fontSize: 11.5 }}>Chargement des créneaux…</div>}
          {slots?.length === 0 && <div style={{ ...T.sub, fontSize: 11.5 }} data-testid="profile-no-slots">Plus de créneau aujourd'hui — prenez rendez-vous pour un autre jour.</div>}
          {slots?.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="profile-slots">
              {slots.map((s) => (
                <button key={s.time} onClick={() => onBook(member)} data-testid={`profile-slot-${s.time}`}
                  style={{ ...T.chip, cursor: "pointer", borderColor: "#0E8A4F", color: "#0E8A4F", fontWeight: 800 }}>
                  Aujourd'hui · {s.time}
                </button>
              ))}
            </div>
          )}
        </div>

        <button style={{ ...T.btnGreen, width: "100%", marginTop: 16, padding: "13px 16px", borderRadius: 12 }} onClick={() => onBook(member)} data-testid="profile-book-btn">
          <CalendarCheck size={15} /> Prendre rendez-vous
        </button>
      </div>
    </div>
  );
};
